import { loadRecaptcha } from '@/platform/recaptcha/browser'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { setAnalyticsEnabled, trackPage } from './segment'

declare global {
  interface Window {
    hj?: ((...args: unknown[]) => void) & { q?: unknown[][] }
    _hjSettings?: { hjid: number; hjsv: number }
  }
}

let hotjarInitialized = false

export function requiresTrackingReload(hotjar: boolean) {
  return hotjarInitialized && !hotjar
}

function isTrackedPath(path: string) {
  return /^\/(?:pt-BR\/)?(?:change-phone|doc-upload)(?:\/|$)/.test(path)
}

function initializeHotjar() {
  const id = Number(process.env.NEXT_PUBLIC_HJID)
  const version = Number(process.env.NEXT_PUBLIC_HJSV)
  if (hotjarInitialized || !id || !version) return
  const queue: unknown[][] = []
  window.hj ??= Object.assign(
    (...args: unknown[]) => {
      queue.push(args)
    },
    { q: queue },
  )
  window._hjSettings = { hjid: id, hjsv: version }
  const script = document.createElement('script')
  script.id = 'hotjar-tracking'
  script.async = true
  script.src = `https://static.hotjar.com/c/hotjar-${id}.js?sv=${version}`
  document.head.appendChild(script)
  hotjarInitialized = true
}

export function BrowserIntegrations({
  path,
  analytics = false,
  hotjar = false,
  recaptcha = false,
}: {
  path: string
  analytics?: boolean
  hotjar?: boolean
  recaptcha?: boolean
}) {
  const router = useRouter()
  useEffect(() => {
    if (requiresTrackingReload(hotjar)) window.location.replace(path)
  }, [hotjar, path])

  useEffect(() => {
    if (recaptcha) void loadRecaptcha().catch(() => undefined)
  }, [recaptcha])

  useEffect(() => {
    let current = true
    setAnalyticsEnabled(analytics)
    if (analytics) void trackPage(path, () => current)
    return () => {
      current = false
      setAnalyticsEnabled(false)
    }
  }, [analytics, path])

  useEffect(() => {
    if (!hotjar) return
    initializeHotjar()
    if (!hotjarInitialized) return

    // Hotjar keeps recording after an SPA URL change. A new document ends its lifetime.
    function onClick(event: MouseEvent) {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
      )
        return
      const link =
        event.target instanceof Element ? event.target.closest('a') : null
      if (!link || link.target === '_blank' || link.hasAttribute('download'))
        return
      const destination = new URL(link.href, window.location.href)
      if (
        destination.origin !== window.location.origin ||
        isTrackedPath(destination.pathname)
      )
        return
      event.preventDefault()
      event.stopPropagation()
      window.location.assign(destination.href)
    }
    document.addEventListener('click', onClick, true)
    router.beforePopState(({ as }) => {
      if (isTrackedPath(new URL(as, window.location.href).pathname)) return true
      window.location.assign(as)
      return false
    })
    return () => {
      document.removeEventListener('click', onClick, true)
      router.beforePopState(() => true)
    }
  }, [hotjar, router])
  return null
}
